import React, { useState, useEffect, useContext } from "react";
import { db } from "../../firebase";
import { collection, addDoc, doc, updateDoc } from "firebase/firestore";
import { AuthContext } from "../../AuthProvider";
import {
  FaUser,
  FaTimesCircle,
  FaUserTie,
  FaPlus,
  FaTrash,
  FaPencilAlt,
  FaGlobe,
  FaBullhorn,
  FaInfoCircle,
  FaRobot,
  FaLeaf,
} from "react-icons/fa";

const PersonaModal = ({ persona, onClose, onSave }) => {
  const { user } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    // ───── BASIC INFO ─────
    name: "Alex Rivera",
    company: "Reworld",
    role: "Senior Sustainable‑Waste Solutions Consultant",
    email: "alex.rivera@reworld.com",
    phone: "(973) 555‑0184",

    // ───── PERSONALITY & BRANDING ─────
    color: "#059669", // Reworld's green brand hue
    tone: "Consultative",
    language: "English (United States)",

    // ───── SYSTEM PROMPT ─────
    systemPrompt:
      'You are a world-class environmental-solutions expert specializing in sustainable, FIFRA-compliant treated-seed disposal for U.S. agricultural and industrial operations. Draft highly personalized five-step email sequences that match each prospect\'s role, company situation, and professional background while showcasing Reworld\'s solutions. Follow these rules: (1) Email 1 opener concise; (2) Email 2 cites a U.S. case + SEEDs program; (3) Email 4 is a consultative offer—no "complimentary" language; (4) Email 5 broadens beyond FIFRA seeds without break-up tone; (5) Every email references U.S. regulations/logistics and the prospect\'s company or location; (6) Subject lines: 2-5 words that spark curiosity or value and include a name, company, or pain-point cue; (7) Word caps: Email 1 ≤ 90 words, Emails 2-4 ≤ 70, Email 5 ≤ 60; (8) Use 3-5 short sentences or crisp bullet points—no bulky paragraphs; (9) One-sentence value prop answers "Why care?" early; (10) First explicit CTA appears no earlier than Email 2; (11) Tone: human, conversational, professional.',

    // ───── INDUSTRY INFO ─────
    industryInfo: `# Treated Seed Disposal Email Sequence Generator  
*(Assume every prospect operates in the United States.)*

## REWORLD WASTE BACKGROUND  
Reworld Waste (formerly Covanta) has 40+ years of experience turning complex waste streams into positive U.S. environmental outcomes:  
- **20 M t** waste managed sustainably in the U.S. each year  
- **19 M t** repurposed as fuel, avoiding landfill use  
- **1.8 M t** recycled or reused  
- **10 M MWh** energy generated from waste  
- Nationwide network of **EPA-permitted** facilities

## TREATED SEED DISPOSAL CHALLENGES (U.S. CONTEXT)  
- Treated seeds contain systemic pesticides (neonicotinoids, fungicides) that persist in the environment.  
- **U.S. EPA** (FIFRA) mandates certified destruction—landfill & compost banned.  
- Traditional landfill/incineration is costly, risks soil/water contamination, and carries penalties.  
- Conventional disposal costs **15-20 %** more than sustainable alternatives.`,

    // ───── VALUE PROP ─────
    sellingPoints: `• Zero‑waste‑to‑landfill (ReDirect360) converts compliance costs into ESG wins.
• Integrated logistics (ReMove) means one invoice, full chain‑of‑custody.
• Waste‑to‑energy plants turn post‑recycled refuse into carbon‑negative power.
• ReCredit offsets help hit Science‑Based‑Target commitments.`,

    // ───── EMAIL SIGNATURE ─────
    signature: `Alex Rivera  |  Senior Sustainable‑Waste Solutions Consultant
Reworld™  •  Morristown, NJ
M (973) 555‑0184  •  alex.rivera@reworld.com
"Others see waste. We see potential."`,

    // ───── CTAs ─────
    callsToAction: [
      "Open to a quick diagnostic of your landfill exposure?",
      "Worth a 15‑min call to see if ReDirect360 could retire a few of your Scope 3 tonnes?",
      "Can I send a one‑pager on how peers cut disposal OPEX 30%?",
      "Is a landfill‑free certification on your 2025 roadmap?",
    ],

    // ───── FEATURE ↔ PAIN MAPPING ─────
    features: [
      {
        solution:
          "ReDirect360 – turnkey zero‑waste‑to‑landfill with real‑time diversion dashboards.",
        painPoint:
          "Monsanto faces shareholder pressure to prove landfill diversion, but current haulers provide no auditable data.",
      },
      {
        solution:
          "ReKiln – engineered fuel derived from non‑recyclable ag‑film and seed‑bag waste.",
        painPoint:
          "Contaminated plastics are landfilled because they fail resin‑quality specs for traditional recyclers.",
      },
      {
        solution:
          "ReMove – dedicated bulk‑waste fleet with DOT‑hazmat credentials.",
        painPoint:
          "Multiple third‑party transporters raise risk and costs for hazardous by‑products.",
      },
    ],

    // ───── PROOF‑POINTS ─────
    proofPoints: [
      "2.2 M tons of waste diverted from landfill annually",
      "40+ waste‑to‑energy and material‑processing facilities across North America",
      "Top‑quartile TRIR safety record (0.62 vs. industry 2.1)",
    ],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newCTA, setNewCTA] = useState("");
  const [newProofPoint, setNewProofPoint] = useState("");
  const [editingFeatureIndex, setEditingFeatureIndex] = useState(null);
  const [newFeature, setNewFeature] = useState({ solution: "", painPoint: "" });

  const toneOptions = [
    "Professional",
    "Casual",
    "Friendly",
    "Formal",
    "Authoritative",
    "Witty",
    "Consultative",
    "Direct",
  ];

  const languageOptions = [
    "English (United States)",
    "English (United Kingdom)",
    "Spanish",
    "French",
    "German",
    "Portuguese",
    "Italian",
    "Dutch",
  ];

  const colorOptions = [
    { label: "Purple", value: "#6D28D9" },
    { label: "Blue", value: "#2563EB" },
    { label: "Green", value: "#059669" },
    { label: "Red", value: "#DC2626" },
    { label: "Orange", value: "#D97706" },
    { label: "Pink", value: "#DB2777" },
    { label: "Indigo", value: "#4F46E5" },
  ];

  useEffect(() => {
    if (persona) {
      setFormData({
        name: persona.name || "",
        company: persona.company || "",
        role: persona.role || "",
        email: persona.email || "",
        phone: persona.phone || "",
        color: persona.color || "#6D28D9",
        tone: persona.tone || "Professional",
        language: persona.language || "English (United States)",
        systemPrompt: persona.systemPrompt || "",
        industryInfo: persona.industryInfo || "",
        sellingPoints: persona.sellingPoints || "",
        signature: persona.signature || "",
        callsToAction: persona.callsToAction || [
          "Can I share how?",
          "Worth a chat?",
          "Would it be crazy for us to schedule a 10-minute call to explore?",
          "Is this a priority for you at the moment?",
        ],
        features: persona.features || [
          {
            solution:
              "Our AI SDR pulls data from 10s of sources to write hyper-personalized emails that get high response rates.",
            painPoint:
              "Human SDRs are expensive and the work they do is repetitive.",
          },
        ],
        proofPoints: persona.proofPoints || [
          "We are backed by top VCs including Y Combinator and HubSpot.",
        ],
      });
    }
  }, [persona]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle CTA additions and deletions
  const addCallToAction = () => {
    if (!newCTA.trim()) return;
    setFormData((prev) => ({
      ...prev,
      callsToAction: [...prev.callsToAction, newCTA],
    }));
    setNewCTA("");
  };

  const removeCallToAction = (index) => {
    setFormData((prev) => ({
      ...prev,
      callsToAction: prev.callsToAction.filter((_, i) => i !== index),
    }));
  };

  // Handle proof point additions and deletions
  const addProofPoint = () => {
    if (!newProofPoint.trim()) return;
    setFormData((prev) => ({
      ...prev,
      proofPoints: [...prev.proofPoints, newProofPoint],
    }));
    setNewProofPoint("");
  };

  const removeProofPoint = (index) => {
    setFormData((prev) => ({
      ...prev,
      proofPoints: prev.proofPoints.filter((_, i) => i !== index),
    }));
  };

  // Handle feature/pain point editing
  const startEditingFeature = (index) => {
    setEditingFeatureIndex(index);
    setNewFeature({
      solution: formData.features[index].solution,
      painPoint: formData.features[index].painPoint,
    });
  };

  const saveFeature = () => {
    if (editingFeatureIndex === null) {
      // Add new feature
      if (!newFeature.solution.trim() && !newFeature.painPoint.trim()) return;

      setFormData((prev) => ({
        ...prev,
        features: [...prev.features, { ...newFeature }],
      }));
    } else {
      // Update existing feature
      setFormData((prev) => ({
        ...prev,
        features: prev.features.map((feature, i) =>
          i === editingFeatureIndex ? { ...newFeature } : feature
        ),
      }));
    }
    setNewFeature({ solution: "", painPoint: "" });
    setEditingFeatureIndex(null);
  };

  const removeFeature = (index) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.name || !formData.email) {
        throw new Error("Name and email are required");
      }

      if (!user) {
        throw new Error("You must be logged in to save personas");
      }

      // Prepare persona data
      const personaData = {
        ...formData,
        updatedAt: new Date(),
      };

      if (persona) {
        // Update existing persona
        const personaRef = doc(
          db,
          "userContent",
          user.uid,
          "aiSdr",
          "personas",
          persona.id
        );
        await updateDoc(personaRef, personaData);
      } else {
        // Create new persona
        personaData.createdAt = new Date();
        await addDoc(
          collection(db, "userContent", user.uid, "aiSdr", "personas"),
          personaData
        );
      }

      // Call the onSave callback to refresh the personas list
      if (onSave) onSave();
      onClose();
    } catch (err) {
      console.error("Error saving persona:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">
            {persona ? "Edit Persona" : "Create New Persona"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <FaTimesCircle size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {/* Persona Color Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Persona Color
              </label>
              <div className="flex flex-wrap gap-2">
                {colorOptions.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() =>
                      setFormData({ ...formData, color: color.value })
                    }
                    className={`w-8 h-8 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                      formData.color === color.value
                        ? "ring-2 ring-offset-2 ring-gray-400"
                        : ""
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.label}
                  />
                ))}
              </div>
            </div>

            {/* Persona Basics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Full name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <input
                  type="text"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Job title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Company name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Email address"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tone
                </label>
                <select
                  name="tone"
                  value={formData.tone}
                  onChange={handleChange}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  {toneOptions.map((tone) => (
                    <option key={tone} value={tone}>
                      {tone}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Campaign Language */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Campaign Language
              </label>
              <div className="flex items-center">
                <FaGlobe className="text-gray-400 mr-2" />
                <select
                  name="language"
                  value={formData.language}
                  onChange={handleChange}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  {languageOptions.map((language) => (
                    <option key={language} value={language}>
                      {language}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* System Prompt */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  <div className="flex items-center">
                    <FaRobot className="mr-2 text-gray-500" />
                    System Prompt
                  </div>
                </label>
                <div className="text-xs text-gray-500 flex items-center">
                  <FaInfoCircle className="mr-1" />
                  Instructions for the AI on how to write emails
                </div>
              </div>
              <textarea
                name="systemPrompt"
                value={formData.systemPrompt}
                onChange={handleChange}
                placeholder="Instructions for the AI on how to write emails..."
                rows={5}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
              />
            </div>

            {/* Industry Information */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  <div className="flex items-center">
                    <FaLeaf className="mr-2 text-gray-500" />
                    Industry Information
                  </div>
                </label>
                <div className="text-xs text-gray-500 flex items-center">
                  <FaInfoCircle className="mr-1" />
                  Background knowledge for the AI
                </div>
              </div>
              <textarea
                name="industryInfo"
                value={formData.industryInfo}
                onChange={handleChange}
                placeholder="Industry-specific background knowledge for the AI..."
                rows={6}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
              />
            </div>

            {/* Key Selling Points */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Key Selling Points
              </label>
              <textarea
                name="sellingPoints"
                value={formData.sellingPoints}
                onChange={handleChange}
                placeholder="What are your main value propositions and selling points?"
                rows={4}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Calls To Action */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  <div className="flex items-center">
                    <FaBullhorn className="mr-2 text-gray-500" />
                    Calls To Action
                  </div>
                </label>
                <div className="text-xs text-gray-500 flex items-center">
                  <FaInfoCircle className="mr-1" />
                  Suggested CTAs for emails
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden mb-3">
                {formData.callsToAction.map((cta, index) => (
                  <div
                    key={index}
                    className="border-b last:border-b-0 p-3 flex justify-between items-center"
                  >
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        className="mr-3"
                        checked
                        readOnly
                      />
                      <span>{cta}</span>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => removeCallToAction(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <FaTrash size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex">
                <input
                  type="text"
                  value={newCTA}
                  onChange={(e) => setNewCTA(e.target.value)}
                  placeholder="Add a new call to action..."
                  className="flex-1 p-2 border rounded-l focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={addCallToAction}
                  className="bg-purple-600 text-white px-4 py-2 rounded-r hover:bg-purple-700 flex items-center"
                >
                  <FaPlus className="mr-1" /> Add CTA
                </button>
              </div>
            </div>

            {/* Features and Pain Points */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Features & Pain Points
                </label>
                <div className="text-xs text-gray-500">
                  Describe your solution and the problems it solves
                </div>
              </div>

              {formData.features.map((feature, index) => (
                <div key={index} className="border rounded-lg p-4 mb-3">
                  <div className="mb-3">
                    <div className="text-sm font-medium text-gray-700 mb-1">
                      Solution
                    </div>
                    <p className="text-gray-800">{feature.solution}</p>
                  </div>
                  <div className="mb-3">
                    <div className="text-sm font-medium text-gray-700 mb-1">
                      Pain Point
                    </div>
                    <p className="text-gray-800">{feature.painPoint}</p>
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => removeFeature(index)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Delete <FaTrash className="inline ml-1" size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={() => startEditingFeature(index)}
                      className="text-blue-500 hover:text-blue-700 text-sm"
                    >
                      Edit <FaPencilAlt className="inline ml-1" size={12} />
                    </button>
                  </div>
                </div>
              ))}

              {editingFeatureIndex !== null ? (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Solution
                    </label>
                    <textarea
                      value={newFeature.solution}
                      onChange={(e) =>
                        setNewFeature({
                          ...newFeature,
                          solution: e.target.value,
                        })
                      }
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      rows={2}
                      placeholder="Describe your solution"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Pain Point
                    </label>
                    <textarea
                      value={newFeature.painPoint}
                      onChange={(e) =>
                        setNewFeature({
                          ...newFeature,
                          painPoint: e.target.value,
                        })
                      }
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      rows={2}
                      placeholder="Describe the pain point this solves"
                    />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingFeatureIndex(null);
                        setNewFeature({ solution: "", painPoint: "" });
                      }}
                      className="px-3 py-1 border rounded text-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={saveFeature}
                      className="px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700"
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setEditingFeatureIndex(null);
                    setNewFeature({ solution: "", painPoint: "" });
                  }}
                  className="flex items-center justify-center w-full p-2 border border-dashed border-gray-300 rounded-lg text-purple-600 hover:text-purple-800 hover:border-purple-300"
                >
                  <FaPlus className="mr-2" /> Add Feature
                </button>
              )}
            </div>

            {/* Proof Points */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Proof Points
                </label>
                <div className="text-xs text-gray-500">
                  Add credibility statements and customer testimonials
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden mb-3">
                {formData.proofPoints.map((point, index) => (
                  <div
                    key={index}
                    className="border-b last:border-b-0 p-3 flex justify-between items-center"
                  >
                    <div>{point}</div>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => removeProofPoint(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <FaTrash size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex">
                <input
                  type="text"
                  value={newProofPoint}
                  onChange={(e) => setNewProofPoint(e.target.value)}
                  placeholder="Add a new proof point..."
                  className="flex-1 p-2 border rounded-l focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={addProofPoint}
                  className="bg-purple-600 text-white px-4 py-2 rounded-r hover:bg-purple-700 flex items-center"
                >
                  <FaPlus className="mr-1" /> Add
                </button>
              </div>
            </div>

            {/* Email Signature */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Signature
              </label>
              <textarea
                name="signature"
                value={formData.signature}
                onChange={handleChange}
                placeholder="Your email signature (optional)"
                rows={3}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Error message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded">
                {error}
              </div>
            )}

            {/* Form actions */}
            <div className="flex justify-end pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border rounded-md text-gray-700 bg-white hover:bg-gray-50 mr-2"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 flex items-center"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  "Create Persona"
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PersonaModal;
